import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/format';

import { JOB_TYPE_LABELS as JT_LABELS } from '../constants/jobTypes';
const JOB_TYPE_LABELS = JT_LABELS;
const CATEGORY_LABELS = {
  'legal': '法律合规', 'finance': '财务审计', 'business': '商务拓展',
  'engineering': '工程技术', 'hr': '人力资源', 'supply-chain': '供应链', 'other': '其他',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};

// Configure marked for safe rendering
marked.setOptions({ breaks: true });

// Render comment content: parse markdown, make images clickable for lightbox
function CommentContent({ content, onImageClick }) {
  const html = marked(content || '');
  const handleClick = useCallback((e) => {
    if (e.target.tagName === 'IMG') {
      onImageClick(e.target.src);
    }
  }, [onImageClick]);

  return (
    <div
      className="comment-body comment-markdown"
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ cursor: 'default' }}
    />
  );
}

// Lightbox overlay for full-size image preview
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!src) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'zoom-out',
      }}
    >
      <img
        src={src}
        alt="预览"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '90vw', maxHeight: '90vh',
          objectFit: 'contain', borderRadius: 8,
          boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
          cursor: 'default',
        }}
      />
      <button
        onClick={onClose}
        style={{
          position: 'fixed', top: 20, right: 28,
          background: 'none', border: 'none',
          color: '#fff', fontSize: 32, cursor: 'pointer',
          lineHeight: 1,
        }}
        aria-label="关闭"
      >
        ×
      </button>
    </div>
  );
}

function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  // New state: submit loading, preview mode, image upload, lightbox
  const [submitting, setSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const textareaRef = useRef(null);

  useEffect(() => {
    fetchJob();
    fetchComments();
  }, [id]);

  const fetchJob = async () => {
    try {
      const res = await api.get(`/jobs/${id}`);
      setJob(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchComments = async () => {
    try {
      const res = await api.get(`/comments/job/${id}`);
      setComments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/comments', { jobId: id, content: newComment, isAnonymous });
      setNewComment('');
      setPreviewMode(false);
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.error || '评论失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId) => {
    if (!replyContent.trim()) return;
    try {
      await api.post('/comments', { jobId: id, content: replyContent, parentId });
      setReplyContent('');
      setReplyTo(null);
      fetchComments();
    } catch (err) {
      alert(err.response?.data?.error || '回复失败');
    }
  };

  // Paste image handler
  const handlePaste = async (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        setImageUploading(true);
        try {
          const formData = new FormData();
          formData.append('image', file);
          const res = await api.post('/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const url = res.data.url;
          // Insert markdown image at cursor position
          const textarea = textareaRef.current;
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const before = newComment.substring(0, start);
            const after = newComment.substring(end);
            const insertion = `![图片](${url})`;
            setNewComment(before + insertion + after);
            // Restore cursor position after state update
            setTimeout(() => {
              textarea.selectionStart = start + insertion.length;
              textarea.selectionEnd = start + insertion.length;
              textarea.focus();
            }, 0);
          } else {
            setNewComment(prev => prev + `![图片](${url})`);
          }
          showToast('图片上传成功');
        } catch (err) {
          showToast('图片上传失败，请重试');
        } finally {
          setImageUploading(false);
        }
        break; // only handle the first image
      }
    }
  };

  const handleShare = (type) => {
    const url = window.location.href;
    const text = `${job.title} - ${job.company} | RollSleeves 涉外岗位`;

    if (type === 'copy') {
      navigator.clipboard.writeText(url);
      showToast('链接已复制');
    } else if (type === 'wechat') {
      navigator.clipboard.writeText(`${text}\n${url}`);
      showToast('已复制到剪贴板，打开微信粘贴发送给好友');
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  if (loading) return <div className="loading">加载中...</div>;
  if (!job) return <div className="empty-state"><p>岗位不存在</p></div>;

  return (
    <div className="job-detail">
      <div className="job-detail-header">
        <Link to="/jobs" style={{ color: 'var(--text-light)', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← 返回岗位列表
        </Link>
        <h1 className="job-detail-title" style={{ marginTop: 16 }}>{job.title}</h1>
        <div className="job-detail-company">{job.company}</div>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-light)', margin: '8px 0' }}>
          📍 {job.location}
          {job.publishDate && <span style={{ marginLeft: 16 }}>📅 发布日期：{job.publishDate}</span>}
        </div>
        <div className="job-detail-tags">
          <span className="tag">{JOB_TYPE_LABELS[job.jobType] || job.jobType}</span>
          <span className="tag tag-purple">{CATEGORY_LABELS[job.category] || job.category}</span>
          {job.salary && <span className="tag tag-amber">{job.salary}</span>}
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
          发布者: {job.authorName || job.authorUsername} · {job.viewCount || 0} 次查看
          {job.publishDate && ` · 发布于 ${formatDate(job.publishDate)}`}
        </div>
      </div>

      {/* Job Info & Contact */}
      <div className="job-detail-sidebar">
        {job.salary && <p><strong>薪资范围：</strong>{job.salary}</p>}
        {user && job.contactInfo && <p><strong>联系方式：</strong>{job.contactInfo}</p>}
        {!user && (
          <div style={{ padding: '12px 16px', background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 8, marginTop: 8 }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-light)' }}>
              🔒 <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600 }}>登录</Link> 后查看联系方式
            </p>
          </div>
        )}
        {user && job.userId !== user.id && (
          <button
            className="btn btn-primary"
            style={{ marginTop: 12, width: '100%' }}
            onClick={() => navigate(`/messages?to=${job.userId}&name=${encodeURIComponent(job.authorName || job.authorUsername)}&jobId=${job.id}&subject=${encodeURIComponent(job.title)}`)}
          >
            私信联系发布者
          </button>
        )}
        {!user && (
          <p style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-light)' }}>
            <Link to="/login" style={{ color: 'var(--primary)' }}>登录</Link> 后可以私信联系发布者
          </p>
        )}
      </div>

      {/* Description */}
      <div className="job-detail-body">
        <h3>岗位描述</h3>
        <pre>{job.description}</pre>

        {job.requirements && (
          <>
            <h3>任职要求</h3>
            <pre>{job.requirements}</pre>
          </>
        )}
      </div>

      {/* Share + Favorite */}
      <div className="share-bar">
        <span>分享此岗位：</span>
        <button className="share-btn" onClick={() => handleShare('copy')}>复制链接</button>
        <button className="share-btn" onClick={() => handleShare('wechat')}>微信分享</button>
        {user && (
          <button
            className="share-btn"
            onClick={async () => {
              try {
                const res = await api.post(`/jobs/${id}/favorite`);
                setJob(j => ({ ...j, isFavorited: res.data.favorited }));
                showToast(res.data.favorited ? '已加入收藏' : '已取消收藏');
              } catch (e) {
                showToast('操作失败');
              }
            }}
            style={{ color: job.isFavorited ? 'var(--error)' : undefined }}
          >
            {job.isFavorited ? '★ 已收藏' : '☆ 收藏'}
          </button>
        )}
      </div>

      {/* Comments */}
      <div className="comments-section">
        <h3>讨论区 ({comments.length} 条评论)</h3>

        {user ? (
          <form onSubmit={handleComment} className="comment-form">
            {/* Preview / Edit toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                style={{
                  fontSize: '0.82rem', padding: '3px 12px', borderRadius: 4,
                  border: '1px solid var(--border, #ddd)',
                  background: !previewMode ? 'var(--primary)' : 'transparent',
                  color: !previewMode ? '#fff' : 'var(--text-light)',
                  cursor: 'pointer',
                }}
              >
                编辑
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode(true)}
                style={{
                  fontSize: '0.82rem', padding: '3px 12px', borderRadius: 4,
                  border: '1px solid var(--border, #ddd)',
                  background: previewMode ? 'var(--primary)' : 'transparent',
                  color: previewMode ? '#fff' : 'var(--text-light)',
                  cursor: 'pointer',
                }}
              >
                预览
              </button>
              {imageUploading && (
                <span style={{ fontSize: '0.82rem', color: 'var(--text-light)', alignSelf: 'center', marginLeft: 8 }}>
                  上传中...
                </span>
              )}
            </div>

            {previewMode ? (
              <div
                className="comment-markdown comment-preview-box"
                dangerouslySetInnerHTML={{ __html: marked(newComment || '') }}
                style={{
                  minHeight: 80, padding: '10px 12px',
                  border: '1px solid var(--border, #ddd)', borderRadius: 6,
                  background: 'var(--bg-secondary, #fafafa)',
                  fontSize: '0.95rem', lineHeight: 1.6,
                }}
                onClick={(e) => {
                  if (e.target.tagName === 'IMG') setLightboxSrc(e.target.src);
                }}
              />
            ) : (
              <textarea
                ref={textareaRef}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onPaste={handlePaste}
                placeholder="分享你对这个岗位的看法、经验或问题...（支持 Markdown，可直接粘贴图片）"
                disabled={imageUploading}
              />
            )}

            <div className="comment-form-actions">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', color: 'var(--text-light)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={e => setIsAnonymous(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                匿名发布
              </label>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={submitting || imageUploading}
                style={{ opacity: (submitting || imageUploading) ? 0.7 : 1 }}
              >
                {submitting ? '发表中...' : '发表评论'}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ padding: '16px 0', color: 'var(--text-light)', fontSize: '0.9rem' }}>
            <Link to="/login" style={{ color: 'var(--primary)' }}>登录</Link> 后参与讨论
          </div>
        )}

        {comments.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-light)' }}>
            暂无评论，成为第一个留言的人
          </div>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="comment">
              <div className="comment-header">
                <div className="comment-avatar">
                  {(comment.authorName || '?')[0].toUpperCase()}
                </div>
                <span className="comment-author">{comment.authorName}</span>
                <span className="comment-time">{formatDateTime(comment.createdAt)}</span>
              </div>
              <CommentContent content={comment.content} onImageClick={setLightboxSrc} />
              <div className="comment-actions">
                {user && (
                  <button className="comment-reply-btn" onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}>
                    回复
                  </button>
                )}
              </div>

              {/* Reply form */}
              {replyTo === comment.id && (
                <div style={{ marginLeft: 42, marginTop: 8 }}>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={replyContent}
                    onChange={e => setReplyContent(e.target.value)}
                    placeholder={`回复 ${comment.authorName}...`}
                    style={{ fontSize: '0.85rem', minHeight: 60 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setReplyTo(null)}>取消</button>
                    <button className="btn btn-primary btn-sm" onClick={() => handleReply(comment.id)}>回复</button>
                  </div>
                </div>
              )}

              {/* Replies */}
              {comment.replies && comment.replies.length > 0 && (
                <div className="comment-replies">
                  {comment.replies.map(reply => (
                    <div key={reply.id} className="comment" style={{ borderBottom: 'none', paddingBottom: 8 }}>
                      <div className="comment-header">
                        <div className="comment-avatar" style={{ width: 26, height: 26, fontSize: '0.7rem' }}>
                          {(reply.authorName || '?')[0].toUpperCase()}
                        </div>
                        <span className="comment-author" style={{ fontSize: '0.85rem' }}>{reply.authorName}</span>
                        <span className="comment-time">{formatDateTime(reply.createdAt)}</span>
                      </div>
                      <CommentContent content={reply.content} onImageClick={setLightboxSrc} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}

      {/* Lightbox */}
      <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      {/* Inline styles for markdown rendered content */}
      <style>{`
        .comment-markdown img {
          max-width: 100%;
          border-radius: 6px;
          cursor: zoom-in;
          margin: 4px 0;
        }
        .comment-markdown p { margin: 0 0 6px 0; }
        .comment-markdown pre {
          background: #f3f4f6;
          padding: 8px 12px;
          border-radius: 4px;
          overflow-x: auto;
          font-size: 0.85em;
        }
        .comment-markdown code {
          background: #f3f4f6;
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 0.85em;
        }
        .comment-markdown blockquote {
          border-left: 3px solid var(--primary, #6c5ce7);
          margin: 6px 0;
          padding-left: 10px;
          color: var(--text-light, #888);
        }
      `}</style>
    </div>
  );
}

export default JobDetail;
